/**
 * Authentication and session-related Supabase queries
 * Pure async functions - no React, no useQuery
 */

import { supabase } from "@/integrations/supabase/client";
import { User } from "@supabase/supabase-js";
import { getActAsCompanyId } from "@/lib/actAsCompany";

// Types
export interface UserProfile {
  user_id: string;
  company_id: string | null;
}

export interface CompanyBasic {
  id: string;
  name: string;
}

export interface AuthSession {
  user: User | null;
  profile: UserProfile | null;
  company: CompanyBasic | null;
  isGlobalAdmin: boolean;
  /** When global admin is "acting as" a company, this is that company's id/name */
  effectiveCompanyId?: string | null;
  effectiveCompanyName?: string | null;
}

/**
 * Get current user with profile and company info in parallel where possible
 * Eliminates the sequential waterfall: getUser → profile → company → role
 */
export async function getCurrentUserWithProfileAndCompany(): Promise<AuthSession> {
  // First, get the authenticated user
  const { data: { user }, error: userError } = await supabase.auth.getUser();

  if (userError || !user) {
    return {
      user: null,
      profile: null,
      company: null,
      isGlobalAdmin: false,
    };
  }

  // Fetch profile (needed to get company_id)
  const { data: profile, error: profileError } = await supabase
    .from('profiles')
    .select('user_id, company_id')
    .eq('user_id', user.id)
    .single();

  if (profileError || !profile) {
    // User exists but no profile yet (e.g., during initial onboarding)
    return {
      user,
      profile: null,
      company: null,
      isGlobalAdmin: false,
    };
  }

  // Now run company fetch and role check in parallel (async-parallel pattern)
  const [companyResult, roleResult] = await Promise.all([
    // Get company name if company_id exists
    profile.company_id
      ? supabase
          .from('companies')
          .select('id, name')
          .eq('id', profile.company_id)
          .single()
      : Promise.resolve({ data: null, error: null }),
    // Check if user is global admin
    supabase.rpc('has_role', {
      _user_id: user.id,
      _role: 'global_admin',
    }),
  ]);

  const company = companyResult.data as CompanyBasic | null;
  const isGlobalAdmin = roleResult.data === true;

  const session: AuthSession = {
    user,
    profile: profile as UserProfile,
    company,
    isGlobalAdmin,
  };

  // Global admin "act as company": override effective company for UI and data
  const actAsId = getActAsCompanyId();
  if (isGlobalAdmin && actAsId) {
    const { data: actAsCompany } = await supabase
      .from("companies")
      .select("id, name")
      .eq("id", actAsId)
      .single();
    if (actAsCompany) {
      session.effectiveCompanyId = actAsCompany.id;
      session.effectiveCompanyName = actAsCompany.name ?? null;
    }
  }

  return session;
}

/**
 * Get just the current user's company ID (for simpler queries)
 * Returns null if user is not authenticated or has no company
 */
export async function getCurrentUserCompanyId(): Promise<string | null> {
  const { data: { user } } = await supabase.auth.getUser();

  if (!user) {
    return null;
  }

  const { data: profile } = await supabase
    .from('profiles')
    .select('company_id')
    .eq('user_id', user.id)
    .single();

  return profile?.company_id ?? null;
}

/**
 * Check if current user has a specific role
 */
export async function checkUserRole(role: 'global_admin' | 'company_admin' | 'company_user'): Promise<boolean> {
  const { data: { user } } = await supabase.auth.getUser();

  if (!user) {
    return false;
  }

  const { data, error } = await supabase.rpc('has_role', {
    _user_id: user.id,
    _role: role,
  });

  if (error) {
    console.error('Error checking user role:', error);
    return false;
  }

  return data === true;
}
